// src/pages/administrators/AdministrativeUnits.tsx
import React, {useState, useEffect, CSSProperties } from 'react';
import * as XLSX from 'xlsx';
import {collection, addDoc, getDocs, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import {db } from '../../firebase';

// TypeScript interface for an administrative unit
interface AdminUnit {
  id: string;
  Village: string;
  'Parish/Ward': string;
  'Subcounty/Division': string;
  'District/City': string;
  Region: string;
}

const styles: {[key: string]: CSSProperties } = {
  container: {
    padding: '2rem',
    fontFamily: 'Arial, sans-serif',
  },
  card: {
    backgroundColor: '#fff',
    padding: '2rem',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    marginBottom: '2rem',
  },
  heading: {
    fontSize: '1.5rem',
    marginBottom: '1rem',
  },
  subHeading: {
    fontSize: '1.2rem',
    marginBottom: '1rem',
    borderBottom: '1px solid #eee',
    paddingBottom: '0.5rem',
  },
  uploadSection: {
    marginBottom: '1rem',
  },
  fileInput: {
    marginBottom: '1rem',
  },
  button: {
    padding: '0.5rem 1rem',
    backgroundColor: '#007BFF',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    marginRight: '0.5rem',
  },
  searchBar: {
    width: '100%',
    padding: '0.5rem',
    marginBottom: '1rem',
    borderRadius: '4px',
    border: '1px solid #ccc',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '1rem',
  },
  th: {
    textAlign: 'left',
    padding: '0.75rem',
    borderBottom: '2px solid #ddd',
  },
  td: {
    padding: '0.75rem',
    borderBottom: '1px solid #eee',
  },
  editInput: {
    width: '100%',
    padding: '0.25rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
  },
  status: {
    marginTop: '1rem',
    padding: '0.75rem',
    borderRadius: '4px',
  },
  success: {
    backgroundColor: '#d4edda',
    color: '#155724',
  },
  error: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
  },
  info: {
    backgroundColor: '#d1ecf1',
    color: '#0c5460',
  },
  dangerButton: {
    backgroundColor: '#dc3545',
    color: '#fff',
    border: 'none',
    padding: '0.5rem',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  editButton: {
    backgroundColor: '#ffc107',
    color: '#fff',
    border: 'none',
    padding: '0.5rem',
    borderRadius: '4px',
    cursor: 'pointer',
    marginRight: '0.5rem',
  },
  addButton: {
    backgroundColor: '#28a745',
    color: '#fff',
    border: 'none',
    padding: '0.5rem',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  form: {
    display: 'grid',
    gap: '1rem',
  },
  formInput: {
    padding: '0.5rem',
    borderRadius: '4px',
    border: '1px solid #ccc',
  },
};

const AdministrativeUnits = () => {
  const [file, setFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploading, setUploading] = useState(false);
  const [units, setUnits] = useState<AdminUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingUnitId, setEditingUnitId] = useState<string | null>(null);
  const [editingData, setEditingData] = useState<Partial<AdminUnit>>({});
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newUnitData, setNewUnitData] = useState<Partial<AdminUnit>>({});

  useEffect(() => {
    fetchUnits();
  }, []);

  const fetchUnits = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'administrativeUnits'));
      const unitsData = querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
      })) as AdminUnit[];
      setUnits(unitsData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching units:", error);
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.name.match(/\.(xlsx|xls)$/)) {
      setFile(selectedFile);
      setUploadStatus(`File selected: ${selectedFile.name}`);
    } else {
      setFile(null);
      setUploadStatus('Please select a valid Excel file (.xlsx or .xls).');
    }
  };

  const handleExcelUpload = async () => {
    if (!file) {
      setUploadStatus('Please select a file first.');
      return;
    }

    setUploading(true);
    setUploadStatus('Processing data and uploading to Firebase...');

    const reader = new FileReader();
    reader.onload = async (e: ProgressEvent<FileReader>) => {
      try {
        const result = e.target?.result;
        if (result === null) {
          setUploadStatus('Failed to read file.');
          setUploading(false);
          return;
        }

        const data = new Uint8Array(result as ArrayBuffer);
        const workbook = XLSX.read(data, {type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: '',
        });

        if (jsonData.length < 2) {
          setUploadStatus('Excel sheet is empty or has no data rows.');
          setUploading(false);
          return;
        }

        const headers = jsonData[0] as string[];
        const rows = jsonData.slice(1);

        const collectionRef = collection(db, 'administrativeUnits');
        let documentsAdded = 0;

        for (const row of rows) {
          if (row.length > 0) {
            const docData: {[key: string]: any } = {};
            docData[headers[0]] = row[0];
            docData[headers[1]] = row[1];
            docData[headers[2]] = row[2];
            docData[headers[3]] = row[3];
            docData[headers[4]] = row[4];
            
            await addDoc(collectionRef, docData);
            documentsAdded++;
          }
        }

        setUploadStatus(`Data saved successfully! Added ${documentsAdded} units. ✅`);
        await fetchUnits();
      } catch (error) {
        console.error('Error saving data to Firebase:', error);
        setUploadStatus(`Error saving data. Check the console for details. ❌`);
      } finally {
        setUploading(false);
        setFile(null);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this unit?")) {
      try {
        await deleteDoc(doc(db, 'administrativeUnits', id));
        setUnits(units.filter(unit => unit.id !== id));
      } catch (error) {
        console.error("Error deleting document:", error);
      }
    }
  };

  const handleEdit = (unit: AdminUnit) => {
    setEditingUnitId(unit.id);
    setEditingData(unit);
  };

  const handleSaveEdit = async () => {
    if (!editingUnitId) return;

    try {
      const unitRef = doc(db, 'administrativeUnits', editingUnitId);
      await updateDoc(unitRef, editingData);
      setEditingUnitId(null);
      setEditingData({});
      await fetchUnits();
    } catch (error) {
      console.error("Error updating document:", error);
    }
  };

  const handleAddUnit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'administrativeUnits'), newUnitData);
      setNewUnitData({});
      setIsAddingNew(false);
      await fetchUnits();
    } catch (error) {
      console.error("Error adding new document:", error);
    }
  };

 const filteredUnits = units.filter(unit => {
  const q = searchQuery.toLowerCase();
  return (
    String(unit.Village || '').toLowerCase().includes(q) ||
    String(unit['Parish/Ward'] || '').toLowerCase().includes(q) ||
    String(unit['Subcounty/Division'] || '').toLowerCase().includes(q) ||
    String(unit['District/City'] || '').toLowerCase().includes(q) ||
    String(unit.Region || '').toLowerCase().includes(q)
  );
});


  const getStatusStyle = () => {
    if (uploadStatus.includes('successfully')) return {...styles.status, ...styles.success };
    if (uploadStatus.includes('Error') || uploadStatus.includes('valid')) return {...styles.status, ...styles.error };
    return {...styles.status, ...styles.info };
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.heading}>Administrative Units Management</h2>
        
        <h3 style={styles.subHeading}>Upload via Excel</h3>
        <p>
          To bulk-add administrative units, select an Excel sheet with the columns: **Village, Parish/Ward, Subcounty/Division, District/City, and Region**.
        </p>
        <div style={styles.uploadSection}>
          <input
            type="file"
            accept=".xlsx, .xls"
            onChange={handleFileChange}
            style={styles.fileInput}
            disabled={uploading}
          />
          <button
            onClick={handleExcelUpload}
            disabled={!file || uploading}
            style={styles.button}
          >
            {uploading ? 'Uploading...' : 'Upload & Save to Firebase'}
          </button>
        </div>
        {uploadStatus && <div style={getStatusStyle()}>{uploadStatus}</div>}
      </div>

      {/* Manual Add Form */}
      <div style={styles.card}>
        <h3 style={styles.subHeading}>Add New Unit Manually</h3>
        {!isAddingNew ? (
          <button onClick={() => setIsAddingNew(true)} style={styles.addButton}>
            Add a New Unit
          </button>
        ) : (
          <form onSubmit={handleAddUnit} style={styles.form}>
            <input
              type="text"
              placeholder="Village"
              value={newUnitData.Village || ''}
              onChange={(e) => setNewUnitData({...newUnitData, Village: e.target.value })}
              style={styles.formInput}
              required
            />
            <input
              type="text"
              placeholder="Parish/Ward"
              value={newUnitData['Parish/Ward'] || ''}
              onChange={(e) => setNewUnitData({...newUnitData, 'Parish/Ward': e.target.value })}
              style={styles.formInput}
              required
            />
            <input
              type="text"
              placeholder="Subcounty/Division"
              value={newUnitData['Subcounty/Division'] || ''}
              onChange={(e) => setNewUnitData({...newUnitData, 'Subcounty/Division': e.target.value })}
              style={styles.formInput}
              required
            />
            <input
              type="text"
              placeholder="District/City"
              value={newUnitData['District/City'] || ''}
              onChange={(e) => setNewUnitData({...newUnitData, 'District/City': e.target.value })}
              style={styles.formInput}
              required
            />
            <input
              type="text"
              placeholder="Region"
              value={newUnitData.Region || ''}
              onChange={(e) => setNewUnitData({...newUnitData, Region: e.target.value })}
              style={styles.formInput}
              required
            />
            <div style={{display: 'flex', gap: '0.5rem' }}>
              <button type="submit" style={styles.button}>Save New Unit</button>
              <button type="button" onClick={() => setIsAddingNew(false)} style={{...styles.button, backgroundColor: '#6c757d' }}>Cancel</button>
            </div>
          </form>
        )}
      </div>

      {/* Search and Display Table */}
      <div style={styles.card}>
        <h3 style={styles.subHeading}>Existing Administrative Units</h3>
        <input
          type="text"
          placeholder="Search by Village, Parish, District, etc."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchBar}
        />
        {loading ? (
          <p>Loading units...</p>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Village</th>
                <th style={styles.th}>Parish/Ward</th>
                <th style={styles.th}>Subcounty/Division</th>
                <th style={styles.th}>District/City</th>
                <th style={styles.th}>Region</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUnits.length > 0 ? (
                filteredUnits.map((unit) => (
                  <tr key={unit.id}>
                    {editingUnitId === unit.id ? (
                      <>
                        <td>
                          <input
                            type="text"
                            value={editingData.Village || ''}
                            onChange={(e) => setEditingData({...editingData, Village: e.target.value })}
                            style={styles.editInput}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={editingData['Parish/Ward'] || ''}
                            onChange={(e) => setEditingData({...editingData, 'Parish/Ward': e.target.value })}
                            style={styles.editInput}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={editingData['Subcounty/Division'] || ''}
                            onChange={(e) => setEditingData({...editingData, 'Subcounty/Division': e.target.value })}
                            style={styles.editInput}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={editingData['District/City'] || ''}
                            onChange={(e) => setEditingData({...editingData, 'District/City': e.target.value })}
                            style={styles.editInput}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={editingData.Region || ''}
                            onChange={(e) => setEditingData({...editingData, Region: e.target.value })}
                            style={styles.editInput}
                          />
                        </td>
                        <td>
                          <button onClick={handleSaveEdit} style={{...styles.button, backgroundColor: '#28a745'}}>Save</button>
                          <button onClick={() => setEditingUnitId(null)} style={{...styles.button, backgroundColor: '#6c757d'}}>Cancel</button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={styles.td}>{unit.Village}</td>
                        <td style={styles.td}>{unit['Parish/Ward']}</td>
                        <td style={styles.td}>{unit['Subcounty/Division']}</td>
                        <td style={styles.td}>{unit['District/City']}</td>
                        <td style={styles.td}>{unit.Region}</td>
                        <td style={styles.td}>
                          <button onClick={() => handleEdit(unit)} style={styles.editButton}>Edit</button>
                          <button onClick={() => handleDelete(unit.id)} style={styles.dangerButton}>Delete</button>
                        </td>
                      </>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} style={styles.td}>No administrative units found.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdministrativeUnits;